import { cloneDeep } from "lodash";
import React, { useContext } from "react";
import {
	matchPath,
	Route,
	RouteComponentProps,
	Switch,
	useHistory,
	useLocation,
	useParams,
	useRouteMatch,
	withRouter,
} from "react-router-dom";
import { common_utils } from "../../../common/common_utils";
import {
	ClearTokenRequest,
	DiscordAuthToken,
	EventCreationProps,
	GetGuildRolesAndChannelsResult,
	GuildChannelSettingsOverrides,
	GuildData,
	GuildEntityColumns,
	GuildSettings,
	GuildSettingsProps,
	Mentionable,
	OnEventEditFunction,
	OnPollEditFunction,
	PollCreationProps,
	PollDisplayData,
	WebDashboardType,
} from "../../../common/sesh_types";
import {
	UserContext,
	UserData,
	GuildRolesAndChannelContext,
	GuildListContext,
} from "./contexts";
import { DashboardUtils, IGuildDataCache } from "./dashboard_utils";
import EventCreation, { fetchEventData } from "./event_creation";
import { ChannelSettingsPickerForm } from "./forms/channel_settings_picker";
import { AdminPage } from "./forms/improved_components/admin/admin_page";
import {
	AdminEntitiesPage,
	AdminStripePage,
	AdminStatusPageWrapper,
	AdminWaitlistPage,
	AdminLogsPage,
	AdminStatsPage,
	AdminShardsPage,
	AdminBlurbsPage,
	AdminBulkDeleteEventsPage,
	AdminDeleteUserDataPage,
} from "./forms/improved_components/admin/admin_sub_pages";
import { AdminEntitlementsPage } from "./forms/improved_components/admin/admin_entitlements_page";
import EventListingFormWithDSTFix from "./forms/improved_components/dst_fix_page/dst_fix_page_improved";
import { GuildMemberContext } from "./forms/improved_components/hooks/use_guild_members";
import { useTemplateId } from "./forms/improved_components/hooks/use_template_id";
import { UserPreferencesModalContext } from "./forms/improved_components/hooks/use_user_preferences_modal";
import { CalendarSettingsPage } from "./forms/improved_components/listings_form/event/calendar/components/calendar_settings_page";
import { EventListBase } from "./forms/improved_components/listings_form/event/event_list_base";
import PollListingForm from "./forms/improved_components/listings_form/poll_listing_form/poll_listing_form";
import { Navbar } from "./forms/improved_components/navbar";
import { PremiumPage } from "./forms/improved_components/premium_page/premium_page";
import { SeshProContactPage } from "./forms/improved_components/premium_page/sesh_pro_contact_page";
import { StripeRedirectPage } from "./forms/improved_components/premium_page/stripe_redirect_page";
import { RSVPDisplayFormPage } from "./forms/improved_components/rsvp_display_form/rsvp_display_form_page";
import { StatusBanner } from "./forms/improved_components/status_banner";
import { UserPreferencesModal } from "./forms/improved_components/user_preferences/user_preferences_modal";
import { GuildOptionsForm, GuildOptionsFormProps } from "./guild_options";
import { GuildSelectForm } from "./guild_select";
import GoogleRedirectPage from "./guild_settings/google_redirect_page";
import {
	fetchGuildChannelSettings,
	fetchGuildSettings,
	GuildSettingsForm,
} from "./guild_settings/guild_settings";
import PollCreation, { fetchPollData } from "./poll_creation";
import { PollResultsForm } from "./poll_results";
import { trpc } from "./utils/trpc";
import { BlurbWrapper } from "./forms/improved_components/blurb/blurb_wrapper";
import { TimeFinderDashboard } from "./forms/improved_components/time-finder/time_finder_dashboard";
import { SeshToaster } from "./forms/improved_components/sesh_toaster";

// Re-export contexts for backward compatibility
export { UserContext, UserData, GuildRolesAndChannelContext, GuildListContext };

interface SeshRouterProps extends RouteComponentProps {
	sesh_client_id: string;
	initial_token: DiscordAuthToken;
}

interface ListOfGuildsData {
	list: GuildData[];
	already_fetched: boolean;
	currently_being_fetched: boolean;
}

interface SeshRouterState {
	discord_auth_token: DiscordAuthToken;
	user_data: UserData;
	list_of_guilds_data: ListOfGuildsData;
	guild_roles_and_channels_cache: CacheData<GetGuildRolesAndChannelsResult>;
	event_data_cache: CacheData<EventCreationProps>;
	poll_data_cache: CacheData<PollCreationProps>;
	poll_listings_cache: CacheData<PollDisplayData[]>;
	guild_settings_data?: GuildSettingsProps; // only non null when currently on page
	guild_channel_settings_overrides?: GuildChannelSettingsOverrides; // only non null when currently on page
	members: Map<string, Mentionable[]>;
	is_user_preferences_open: boolean;
}

export async function redirectToDiscordAuth(
	client_id: string,
	access_token?: string,
	desired_redirect_uri?: string,
	dashboard_type: WebDashboardType = WebDashboardType.Redirect,
	extra_state?: any
) {
	const prefix = location.protocol + "//" + location.host;
	const base_redirect_uri = encodeURIComponent(prefix + "/dashboard/");

	const state = {
		redirect: desired_redirect_uri,
		dashboard_type: dashboard_type,
		extra_state: extra_state,
		code_source: "discord",
	};

	const encoded_state = common_utils.encode_state(state);
	const auth_uri = `https://discord.com/api/oauth2/authorize?client_id=${client_id}&redirect_uri=${base_redirect_uri}&response_type=code&scope=identify%20guilds%20email&state=${encoded_state}&test=hello`;

	localStorage.clear();

	if (access_token) {
		const clear_token_body: ClearTokenRequest = {
			access_token,
		};

		await fetch(`/api/clear_token`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify(clear_token_body),
		}).catch(() => {});
	}

	window.location.replace(auth_uri);
}

function InitGuildSelect(props: {
	list_of_guilds: GuildData[];
	discord_auth_token: DiscordAuthToken;
	sesh_client_id: string;
}) {
	if (!props.list_of_guilds) {
		return DashboardUtils.RenderFullLoadingThrobberPage();
	}

	return (
		<GuildSelectForm
			list_of_guilds={props.list_of_guilds}
			auth_token={props.discord_auth_token}
			bot_client_id={props.sesh_client_id}
		/>
	);
}

function InitPollListings() {
	return <PollListingForm />;
}

function InitGuildSettings(props: {
	discord_auth_token: DiscordAuthToken;
	settings_data: GuildSettingsProps;
	roles_and_channels_cache: Map<string, GetGuildRolesAndChannelsResult>;
	clear_settings_data_func: Function;
	on_save_func: (
		new_settings: GuildSettings,
		individual_channel_settings_overrides?: GuildEntityColumns
	) => void;
	on_premium_modified_func: GuildSettingsProps["on_premium_modified_func"];
}) {
	let { server_id } = useParams<{ server_id: string }>();

	const history = useHistory();
	const location = useLocation();
	const match = useRouteMatch();

	if (
		!props.settings_data ||
		!props.roles_and_channels_cache.has(server_id)
	) {
		return DashboardUtils.RenderFullLoadingThrobberPage();
	}

	const roles_and_channels_data =
		props.roles_and_channels_cache.get(server_id);

	const poll_channel = roles_and_channels_data.channels.find(
		(val) => props.settings_data.initial_poll_channel_id == val.id
	);

	const event_channel = roles_and_channels_data.channels.find(
		(val) => props.settings_data.initial_event_channel_id == val.id
	);

	const guild_settings: GuildSettings = {
		...props.settings_data.original_guild_settings,
		event_channel,
		poll_channel,
	};

	const settings_form_props: GuildSettingsProps = {
		...props.settings_data,
		original_guild_settings: guild_settings,
		all_text_channels: roles_and_channels_data.channels,
		all_voice_channels: roles_and_channels_data.voice_channels,
		roles: roles_and_channels_data.roles,
		sesh_has_manage_roles: roles_and_channels_data.sesh_has_manage_roles,
		sesh_has_manage_events: roles_and_channels_data.sesh_has_manage_events,
		user_has_manage_events: roles_and_channels_data.user_has_manage_events,
		sesh_role_name: roles_and_channels_data.sesh_role_name,
		on_save_func: props.on_save_func,
		on_premium_modified_func: props.on_premium_modified_func,
	};

	return (
		<GuildSettingsForm
			{...settings_form_props}
			auth_token={props.discord_auth_token}
			clear_parents_settings_data_func={props.clear_settings_data_func}
			history={history}
			location={location}
			match={match}
		/>
	);
}

function InitGuildChannelSettings(props: {
	discord_auth_token: DiscordAuthToken;
	channel_settings_overrides: GuildChannelSettingsOverrides;
	guild_settings_data: GuildSettingsProps;
	roles_and_channels_cache: Map<string, GetGuildRolesAndChannelsResult>;
	clear_settings_data_func: Function;
	on_save_func: (
		new_settings: GuildSettings,
		individual_channel_settings_overrides?: GuildEntityColumns,
		channel_id?: string,
		guild_id?: string,
		refetch_channels?: boolean
	) => void;
}) {
	let { server_id, channel_id } = useParams<{
		server_id: string;
		channel_id: string;
	}>();

	const history = useHistory();
	const location = useLocation();
	const match = useRouteMatch();

	if (
		!props.channel_settings_overrides ||
		!props.guild_settings_data ||
		!props.roles_and_channels_cache.has(server_id)
	) {
		return DashboardUtils.RenderFullLoadingThrobberPage();
	}

	const roles_and_channels_data =
		props.roles_and_channels_cache.get(server_id);

	const poll_channel = roles_and_channels_data.channels.find(
		(val) => props.guild_settings_data.initial_poll_channel_id === val.id
	);

	const event_channel = roles_and_channels_data.channels.find(
		(val) => props.guild_settings_data.initial_event_channel_id === val.id
	);

	const guild_settings: GuildSettings = {
		...props.guild_settings_data.original_guild_settings,
		event_channel,
		poll_channel,
	};

	const settings_form_props: GuildSettingsProps = {
		...props.guild_settings_data,
		original_guild_settings: guild_settings,
		all_text_channels: roles_and_channels_data.channels,
		all_voice_channels: roles_and_channels_data.voice_channels,
		roles: roles_and_channels_data.roles,
		sesh_has_manage_roles: roles_and_channels_data.sesh_has_manage_roles,
		sesh_has_manage_events: roles_and_channels_data.sesh_has_manage_events,
		user_has_manage_events: roles_and_channels_data.user_has_manage_events,
		sesh_role_name: roles_and_channels_data.sesh_role_name,
		on_save_func: props.on_save_func,
	};

	const individual_channel_settings_overrides =
		props.channel_settings_overrides.overrides[channel_id];

	const channel_we_are_overriding_settings_for =
		roles_and_channels_data.channels.find((val) => channel_id === val.id);

	if (!channel_we_are_overriding_settings_for) {
		return (
			<DashboardUtils.RenderDashboardErrorPage
				title="Error 404"
				text={`Channel ID ${channel_id} not found`}
			/>
		);
	}

	if (channel_we_are_overriding_settings_for.type === "Thread") {
		return (
			<DashboardUtils.RenderDashboardErrorPage
				title="Error"
				text={`Can not override settings on a thread. Change the parent channel's settings instead.`}
			/>
		);
	}

	return (
		<GuildSettingsForm
			{...settings_form_props}
			auth_token={props.discord_auth_token}
			clear_parents_settings_data_func={props.clear_settings_data_func}
			history={history}
			location={location}
			match={match}
			is_channel_settings_overrides_page={true}
			individual_channel_id={channel_id}
			individual_channel_settings_overrides={
				individual_channel_settings_overrides
			}
		/>
	);
}

function InitGuildChannelPicker(props: {
	list_of_guilds: GuildData[];
	discord_auth_token: DiscordAuthToken;
	channel_settings_data: GuildChannelSettingsOverrides;
	roles_and_channels_cache: Map<string, GetGuildRolesAndChannelsResult>;
	clear_settings_data_func: Function;
	on_save_func: (deleted_override_channel_id: string) => void;
}) {
	let { server_id } = useParams<{ server_id: string }>();

	let history = useHistory();

	const guild_data = props.list_of_guilds.find((val) => val.id === server_id);
	if (!guild_data) {
		DashboardUtils.ShowDefaultErrorAlert(`Invalid server id: ${server_id}`);
		history.replace("/dashboard/");
		return null;
	}

	if (
		!props.channel_settings_data ||
		!props.roles_and_channels_cache.has(server_id)
	) {
		return DashboardUtils.RenderFullLoadingThrobberPage();
	}

	const roles_and_channels_data =
		props.roles_and_channels_cache.get(server_id);

	// const poll_channel = roles_and_channels_data.channels.find(
	// 	(val) => props.settings_data.initial_poll_channel_id == val.id
	// );

	// const event_channel = roles_and_channels_data.channels.find(
	// 	(val) => props.settings_data.initial_event_channel_id == val.id
	// );

	// const attendee_role_data_with_ids =
	// 	props.settings_data.initial_attendee_role_data;

	// const attendee_role_data: AttendeeRoleData = {
	// 	remove_on_end: attendee_role_data_with_ids.remove_on_end,
	// 	role_add_time: attendee_role_data_with_ids.role_add_time,
	// 	roles: attendee_role_data_with_ids.role_ids.map((role_id) =>
	// 		roles_and_channels_data.roles.find((role) => role.id === role_id)
	// 	),
	// };

	// const guild_settings: GuildSettings = {
	// 	...props.settings_data.guild_settings,
	// 	event_channel,
	// 	poll_channel,
	// 	attendee_role_data,
	// };

	// const settings_form_props: GuildSettingsProps = {
	// 	...props.settings_data,
	// 	guild_settings,
	// 	channels: roles_and_channels_data.channels,
	// 	roles: roles_and_channels_data.roles,
	// 	sesh_has_manage_roles: roles_and_channels_data.sesh_has_manage_roles,
	// 	sesh_role_name: roles_and_channels_data.sesh_role_name,
	// };

	return (
		<ChannelSettingsPickerForm
			guild_data={guild_data}
			channel_settings_overrides={props.channel_settings_data}
			roles_and_channels={roles_and_channels_data}
			auth_token={props.discord_auth_token}
			on_save_func={props.on_save_func}
		/>
	);
}

function InitPollResults() {
	let { server_id, message_id } = useParams<{
		server_id: string;
		message_id: string;
	}>();

	const guild_list = useContext(GuildListContext);
	const guild_data = guild_list?.find((val) => val.id === server_id);

	const get_list_item_query = trpc.poll.getListItem.useQuery({
		guild_id: server_id,
		message_id: message_id,
	});

	const options_query = trpc.poll.votesOverview.useQuery({
		guild_id: server_id,
		message_id: message_id,
	});

	const unique_vote_count_query = trpc.poll.uniqueVoteCount.useQuery({
		guild_id: server_id,
		message_id: message_id,
	});

	if (
		!guild_data ||
		!get_list_item_query.isSuccess ||
		!options_query.isSuccess ||
		!unique_vote_count_query.isSuccess
	) {
		return DashboardUtils.RenderFullLoadingThrobberPage();
	}
	// if we do not have voter names, we need to wait for members data
	// if we do have voter names, we do not need to wait

	return (
		<PollResultsForm
			guild_data={guild_data}
			poll={get_list_item_query.data}
			options={options_query.data}
			unique_vote_count={unique_vote_count_query.data}
		/>
	);
}

function InitPollCreation(props: {
	discord_auth_token: DiscordAuthToken;
	roles_and_channels_cache: Map<string, GetGuildRolesAndChannelsResult>;

	poll_data_cache: Map<string, PollCreationProps>;
	is_edit: boolean;
	on_poll_edited_func?: OnPollEditFunction;
	on_unmount?: (server_id) => void;
}) {
	let { server_id, message_id } = useParams<{
		server_id: string;
		message_id: string;
	}>();

	// only wait for poll data
	if (!props.poll_data_cache.has(server_id)) {
		return DashboardUtils.RenderFullLoadingThrobberPage();
	}

	const poll_data = props.poll_data_cache.get(server_id);

	if (message_id) {
		if (poll_data.edit_data?.poll_message_id !== message_id) {
			return DashboardUtils.RenderFullLoadingThrobberPage();
		}
	}

	const roles_and_channels_data =
		props.roles_and_channels_cache.get(server_id);

	const poll_creation_props: PollCreationProps = {
		...poll_data,
		roles: roles_and_channels_data?.roles,
		all_channel_data: roles_and_channels_data?.channels,
		auth_token: props.discord_auth_token,
		is_edit: props.is_edit,
		edit_data: props.is_edit ? poll_data.edit_data : undefined,
		on_unmount: Boolean(props.on_unmount)
			? () => props.on_unmount(server_id)
			: undefined,
		on_poll_edited_func: props.on_poll_edited_func,
	};

	return <PollCreation {...poll_creation_props}></PollCreation>;
}

function InitEventCreation(props: {
	discord_auth_token: DiscordAuthToken;

	roles_and_channels_cache: Map<string, GetGuildRolesAndChannelsResult>;
	event_data_cache: Map<string, EventCreationProps>;
	is_edit: boolean;
	on_event_edited_func?: OnEventEditFunction;
	on_unmount?: (server_id) => void;
}) {
	let { server_id, message_id } = useParams<{
		server_id: string;
		message_id: string;
	}>();

	const [template_id, type, id] = useTemplateId();

	// State is used for event templates to force a rerender when same template is selected
	const state = useLocation().state as { uuid: number };

	// Ensure this is not an edit via checking for message id existence
	const has_template = Boolean(template_id) && !Boolean(message_id);

	const template_query = trpc.event_template.get.useQuery(
		{
			guild_id: server_id,
			message_id: type === "event" ? id : undefined,
			template_id: type === "template" ? id : undefined,
		},
		{
			enabled: has_template,
		}
	);

	// only wait for event data
	if (!props.event_data_cache.has(server_id)) {
		return DashboardUtils.RenderFullLoadingThrobberPage();
	}

	if (has_template && !template_query.isSuccess) {
		return DashboardUtils.RenderFullLoadingThrobberPage();
	}

	const event_data = props.event_data_cache.get(server_id);
	const roles_and_channels_data =
		props.roles_and_channels_cache.get(server_id);

	if (event_data.is_premium && !roles_and_channels_data) {
		return DashboardUtils.RenderFullLoadingThrobberPage();
	}

	if (message_id) {
		if (event_data.edit_data?.event_message_id !== message_id) {
			return DashboardUtils.RenderFullLoadingThrobberPage();
		}
	}

	const event_creation_props: EventCreationProps = {
		...event_data,
		template_data: has_template ? template_query.data : undefined,
		roles: roles_and_channels_data?.roles,
		all_text_channels: roles_and_channels_data?.channels,
		sesh_has_manage_roles: roles_and_channels_data?.sesh_has_manage_roles,
		sesh_has_manage_events: roles_and_channels_data?.sesh_has_manage_events,
		user_has_manage_events: roles_and_channels_data?.user_has_manage_events,
		sesh_role_name: roles_and_channels_data?.sesh_role_name,
		all_voice_channels: roles_and_channels_data?.voice_channels,
		auth_token: props.discord_auth_token,
		is_edit: props.is_edit,
		edit_data: props.is_edit ? event_data.edit_data : undefined,
		on_event_edited_func: props.on_event_edited_func,
		on_unmount: Boolean(props.on_unmount)
			? () => props.on_unmount(server_id)
			: undefined,
	};

	return (
		<EventCreation
			key={template_id ? `${state?.uuid}_${template_id}` : "no-template"}
			{...event_creation_props}
		></EventCreation>
	);
}

function InitGuildOptions(props: {
	list_of_guilds: GuildData[];
	auth_token: DiscordAuthToken;
}) {
	let { server_id } = useParams<{ server_id: string }>();
	let history = useHistory();

	const location = useLocation();
	const match = useRouteMatch();

	if (!props.list_of_guilds) {
		return DashboardUtils.RenderFullLoadingThrobberPage();
	}

	const guild_data = props.list_of_guilds?.find(
		(val) => val.id === server_id
	);

	if (!guild_data) {
		DashboardUtils.ShowDefaultErrorAlert(`Invalid server id: ${server_id}`);
		history.replace("/dashboard/");
		return null;
	}

	const guild_select_props: GuildOptionsFormProps = {
		auth_token: props.auth_token,
		guild_data,
		history,
		location,
		match,
	};

	return <GuildOptionsForm {...guild_select_props} />;
}

interface IPathDataArgs {
	path: string;
	title?: string;
	redirect_url_data?: RedirectURLData;
	exact: boolean;
	switch_func?: (params: PathParams, query: URLSearchParams) => void;
}

interface RedirectURLData {
	path_generate_func: (params: PathParams, query: URLSearchParams) => string;
	urls_that_should_redirect: string[];
}

interface CacheData<T> {
	currently_being_fetched: boolean;
	cache: Map<string, T>;
}

function createCacheData<T>(): CacheData<T> {
	return {
		currently_being_fetched: false,
		cache: new Map<string, T>(),
	};
}

interface PathParams {
	server_id?: string;
	message_id?: string;
	channel_id?: string;
}
class SeshRouter extends React.Component<SeshRouterProps, SeshRouterState> {
	private PathData: { [key: string]: IPathDataArgs } = {
		guild_select: {
			path: "/dashboard",
			exact: true,
			switch_func: (params, query) => {
				this.fetchGuildDataAndUpdateState(true);
			},
			title: "Server Select",
		},
		google_redirect: {
			path: "/dashboard/google",
			exact: true,
		},
		premium: {
			path: "/dashboard/premium",
			exact: true,
			title: "Premium",
		},
		stripe_redirect: {
			path: "/dashboard/premium/stripe",
			exact: true,
		},
		contact_us: {
			path: "/dashboard/contact-us",
			exact: true,
			title: "Contact Us",
		},
		guild_options: {
			path: "/dashboard/:server_id",
			exact: true,
			switch_func: (params, query) => {
				this.fetchGuildDataAndUpdateState(true);
			},
			title: "",
		},
		guild_settings: {
			path: "/dashboard/:server_id/settings",
			exact: true,
			switch_func: (params, query) => {
				this.fetchGuildSettingsAndUpdateState(params.server_id);
				this.fetchGuildRolesAndChannelsAndUpdateState(params.server_id);
			},
			title: "Settings",
		},
		guild_calendar_settings: {
			path: "/dashboard/:server_id/settings/calendar",
			exact: true,
			title: "Settings",
		},
		guild_channel_settings_picker: {
			path: "/dashboard/:server_id/settings/channels",
			exact: true,
			switch_func: (params, query) => {
				this.fetchGuildChannelSettingsAndUpdateState(params.server_id);
				this.fetchGuildRolesAndChannelsAndUpdateState(params.server_id);
			},
			title: "Settings",
		},
		guild_channel_settings: {
			path: "/dashboard/:server_id/settings/channels/:channel_id",
			exact: true,
			switch_func: (params, query) => {
				this.fetchGuildSettingsAndUpdateState(params.server_id);
				this.fetchGuildChannelSettingsAndUpdateState(params.server_id);
				this.fetchGuildRolesAndChannelsAndUpdateState(params.server_id);
			},
			title: "Settings",
		},
		guild_channel: {
			path: "/dashboard/:server_id/settings/channels",
			exact: true,
			switch_func: (params, query) => {
				this.fetchGuildChannelSettingsAndUpdateState(params.server_id);
				this.fetchGuildRolesAndChannelsAndUpdateState(params.server_id);
			},
			title: "Settings",
		},
		event_creation: {
			path: "/dashboard/:server_id/events/create",
			redirect_url_data: {
				path_generate_func: (params, query) => {
					const channel_id = query.get("channel_id");
					return common_utils.getEventCreateURL(
						params.server_id,
						channel_id
					);
				},
				urls_that_should_redirect: [
					"/dashboard/:server_id/create_event",
				],
			},
			title: "Creating Event",
			exact: true,
			switch_func: (params, query) => {
				this.fetchEventDataAndUpdateState(params.server_id);
				this.fetchGuildRolesAndChannelsAndUpdateState(params.server_id);
			},
		},
		event_listings: {
			path: "/dashboard/:server_id/events",
			exact: true,
			switch_func: (params, query) => {
				this.fetchGuildRolesAndChannelsAndUpdateState(params.server_id);
			},
			title: "Events",
		},
		time_finder: {
			path: "/dashboard/:server_id/time-finder",
			exact: true,
			title: "Time Finder",
		},
		time_finder_create: {
			path: "/dashboard/:server_id/time-finder/create",
			exact: true,
			title: "Create Time Finder",
			switch_func: (params, query) => {
				this.fetchGuildRolesAndChannelsAndUpdateState(params.server_id);
			},
		},
		time_finder_view: {
			path: "/dashboard/:server_id/time-finder/view/:instance_id",
			exact: true,
			title: "View Time Finder",
			switch_func: (params, query) => {
				this.fetchGuildRolesAndChannelsAndUpdateState(params.server_id);
			},
		},
		time_finder_list: {
			path: "/dashboard/:server_id/time-finder/view",
			exact: true,
			title: "Time Finder List",
		},
		time_finder_edit: {
			path: "/dashboard/:server_id/time-finder/edit/:instance_id",
			exact: true,
			title: "Edit Time Finder",
			switch_func: (params, query) => {
				this.fetchGuildRolesAndChannelsAndUpdateState(params.server_id);
			},
		},
		event_attendees: {
			path: "/dashboard/:server_id/events/attendees/:message_id",
			exact: true,
			title: "Event Attendees",
		},
		event_editing: {
			path: "/dashboard/:server_id/events/edit/:message_id",
			redirect_url_data: {
				path_generate_func: (params) =>
					common_utils.getEventEditURL(
						params.server_id,
						params.message_id
					),
				urls_that_should_redirect: [
					"/dashboard/:server_id/edit_event/:message_id",
				],
			},
			title: "Editing Event",
			exact: true,
			switch_func: (params, query) => {
				this.fetchEventDataAndUpdateState(
					params.server_id,
					params.message_id
				);
				this.fetchGuildRolesAndChannelsAndUpdateState(params.server_id);
			},
		},
		poll_creation: {
			path: "/dashboard/:server_id/polls/create",
			redirect_url_data: {
				path_generate_func: (params, query) => {
					const channel_id = query.get("channel_id");
					return common_utils.getPollCreateURL(
						params.server_id,
						channel_id
					);
				},
				urls_that_should_redirect: [
					"/dashboard/:server_id/create_poll",
				],
			},
			exact: true,
			switch_func: (params, query) => {
				this.fetchPollDataAndUpdateState(
					params.server_id,
					params.message_id
				);
				this.fetchGuildRolesAndChannelsAndUpdateState(params.server_id);
			},
			title: "Creating Poll",
		},
		poll_listings: {
			path: "/dashboard/:server_id/polls",
			exact: true,
			switch_func: (params, query) => {
				this.fetchGuildRolesAndChannelsAndUpdateState(params.server_id);
			},
			title: "Polls",
		},
		poll_editing: {
			path: "/dashboard/:server_id/polls/edit/:message_id",
			exact: true,
			redirect_url_data: {
				path_generate_func: (params) =>
					common_utils.getPollEditURL(
						params.server_id,
						params.message_id
					),
				urls_that_should_redirect: [
					"/dashboard/:server_id/edit_poll/:message_id",
				],
			},
			switch_func: (params, query) => {
				this.fetchPollDataAndUpdateState(
					params.server_id,
					params.message_id
				);
				this.fetchGuildRolesAndChannelsAndUpdateState(params.server_id);
			},
			title: "Editing Poll",
		},
		poll_results: {
			path: "/dashboard/:server_id/polls/results/:message_id",
			redirect_url_data: {
				path_generate_func: (params) =>
					common_utils.getPollResultsURL(
						params.server_id,
						params.message_id
					),
				urls_that_should_redirect: [
					"/dashboard/:server_id/poll_results/:message_id",
				],
			},
			exact: true,
			title: "Poll Results",
		},
		dst_fix: {
			path: "/dashboard/:server_id/events/dst_fix",
			exact: true,
			switch_func: (params, query) => {
				this.fetchGuildRolesAndChannelsAndUpdateState(params.server_id);
			},
			title: "DST Fix",
		},
	};

	constructor(props) {
		super(props);

		const cached_user_data =
			DashboardUtils.GetParsedValueFromLocalStorage<UserData>(
				DashboardUtils.USER_DATA_CACHE_KEY
			);

		const guild_data =
			DashboardUtils.GetParsedValueFromLocalStorage<IGuildDataCache>(
				DashboardUtils.GUILD_DATA_CACHE_KEY
			)?.guild_data;

		this.state = {
			discord_auth_token: this.props.initial_token,
			user_data: cached_user_data,
			list_of_guilds_data: {
				list: guild_data,
				already_fetched: false,
				currently_being_fetched: false,
			},
			event_data_cache: createCacheData(),
			poll_data_cache: createCacheData(),
			guild_roles_and_channels_cache: createCacheData(),
			poll_listings_cache: createCacheData(),
			members: new Map(),
			is_user_preferences_open: false,
		};

		this.fetchEventDataAndUpdateState =
			this.fetchEventDataAndUpdateState.bind(this);

		this.onPollCreatedOrEdited = this.onPollCreatedOrEdited.bind(this);
		this.onEventCreatedOrEdited = this.onEventCreatedOrEdited.bind(this);
	}

	componentDidMount() {
		// this.props.history.replace({
		// 	pathname: "/dashboard/",
		// 	search: "?" + new URLSearchParams({ clientId: "test" }).toString(),
		// });

		fetch("https://discord.com/api/users/@me", {
			headers: {
				authorization: `${this.state.discord_auth_token.token_type} ${this.state.discord_auth_token.access_token}`,
			},
		})
			.then(async (response) => {
				let user_response = await response.json();

				if (!user_response.id) {
					redirectToDiscordAuth(
						this.props.sesh_client_id,
						this.state.discord_auth_token.access_token,
						this.props.location.pathname + window.location.search
					);
					return;
				}

				const avatar_uri = user_response.avatar
					? `https://cdn.discordapp.com/avatars/${user_response.id}/${user_response.avatar}.png?size=64`
					: `https://cdn.discordapp.com/embed/avatars/${
							Number(BigInt(user_response.id) >> 22n) % 6
					  }.png`;

				const user_data: UserData = {
					id: user_response.id,
					avatar_uri: avatar_uri,
					discriminator: user_response.discriminator,
					username: user_response.username,
					email: user_response.email,
				};

				localStorage.setItem(
					DashboardUtils.USER_DATA_CACHE_KEY,
					JSON.stringify(user_data)
				);

				this.setState({
					user_data,
				});

				this.matchPathAndCallSwitchFuncs();
				this.fetchGuildDataAndUpdateState(true);
			})
			.catch((err) => {
				console.log(err);
				DashboardUtils.ShowDefaultErrorAlert(
					"Make sure you do not have any browser extensions blocking Discord API calls.",
					"Discord API Fetch Error"
				).then(() => {
					redirectToDiscordAuth(
						this.props.sesh_client_id,
						this.state.discord_auth_token.access_token
					);
				});
			});
	}

	fetchGuildDataAndUpdateState(page_requires_guild_data: boolean = false) {
		// store guild data on sesh router, only fetch if we're on a page that needs it
		// if cache is empty, fetch regardless
		const should_fetch =
			(page_requires_guild_data &&
				!this.state.list_of_guilds_data.already_fetched) ||
			!Boolean(this.state.list_of_guilds_data.list);

		if (!should_fetch) {
			//console.log("Guild List from Cache");
			return;
		}

		if (this.state.list_of_guilds_data.currently_being_fetched) {
			//console.log("Guild List Duplicate Fetch (Aborting)");
			return;
		}

		//console.log("Fetching Guild List");

		// update it instantly because setState is not guaranteed to be instant
		this.state.list_of_guilds_data.currently_being_fetched = true;

		if (this.state.list_of_guilds_data.already_fetched) {
			DashboardUtils.ShowDefaultErrorAlert(
				"Duplicate guild data fetch. Aborting."
			);
			return;
		}

		this.setState({
			list_of_guilds_data: {
				currently_being_fetched: true,
				list: this.state.list_of_guilds_data.list,
				already_fetched: this.state.list_of_guilds_data.already_fetched,
			},
		});

		DashboardUtils.FetchUsersGuildData(this.state.discord_auth_token).then(
			(val) => {
				this.setState({
					list_of_guilds_data: {
						list: val?.props ?? this.state.list_of_guilds_data.list,
						already_fetched: true,
						currently_being_fetched: false,
					},
				});
			}
		);
	}

	componentDidUpdate(prevProps: SeshRouterProps) {
		if (this.props.location.pathname != prevProps.location.pathname) {
			this.matchPathAndCallSwitchFuncs();
		}
	}

	matchPathAndCallSwitchFuncs() {
		const query = new URLSearchParams(location.search);
		for (let k in this.PathData) {
			const current_path_data = this.PathData[k];
			if (current_path_data.switch_func) {
				let path_matches = matchPath<PathParams>(
					this.props.location.pathname,
					{
						path: current_path_data.path,
						exact: current_path_data.exact,
					}
				);

				if (!path_matches && current_path_data.redirect_url_data) {
					for (let url of current_path_data.redirect_url_data
						.urls_that_should_redirect) {
						path_matches = matchPath<PathParams>(
							this.props.location.pathname,
							{
								path: url,
								exact: current_path_data.exact,
							}
						);

						if (path_matches) {
							this.props.history.replace(
								current_path_data.redirect_url_data.path_generate_func(
									path_matches.params,
									query
								)
							);
							//this.props.history.push(current_path_data.path);
							return;
						}
					}
				}

				if (path_matches) {
					current_path_data.switch_func(path_matches.params, query);
					return;
				}
			}
		}
	}

	fetchGuildRolesAndChannelsAndUpdateState(
		guild_id: string,
		force?: boolean
	) {
		if (
			this.state.guild_roles_and_channels_cache.cache.has(guild_id) &&
			!force
		) {
			//console.log(guild_id, "Roles/Channels Cache");
			return;
		}

		if (this.state.guild_roles_and_channels_cache.currently_being_fetched) {
			//console.log(guild_id, "Roles/Channels Duplicate Fetch (Aborting)");
			return;
		}

		//console.log(guild_id, "Fetching Roles/Channels");

		// update it instantly because setState is not guaranteed to be instant
		this.state.guild_roles_and_channels_cache.currently_being_fetched =
			true;

		this.setState({
			guild_roles_and_channels_cache: {
				currently_being_fetched: true,
				cache: this.state.guild_roles_and_channels_cache.cache,
			},
		});

		return DashboardUtils.FetchGuildRolesAndChannels(
			this.state.discord_auth_token,
			guild_id
		).then((val) => {
			this.state.guild_roles_and_channels_cache.cache.set(guild_id, val);

			this.setState({
				guild_roles_and_channels_cache: {
					currently_being_fetched: false,
					cache: this.state.guild_roles_and_channels_cache.cache,
				},
			});
		});
	}

	onEventCreatedOrEdited(guild_id: string) {
		if (!this.state.guild_roles_and_channels_cache.cache.has(guild_id)) {
			return;
		}

		this.state.guild_roles_and_channels_cache.cache.delete(guild_id);
		this.setState({
			guild_roles_and_channels_cache: {
				currently_being_fetched:
					this.state.guild_roles_and_channels_cache
						.currently_being_fetched,
				cache: this.state.guild_roles_and_channels_cache.cache,
			},
		});
	}

	async onPollCreatedOrEdited(
		guild_id: string,
		display_data: PollDisplayData
	) {
		if (!this.state.poll_listings_cache.cache.has(guild_id)) {
			return;
		}

		const roles_and_channels_cache =
			this.state.guild_roles_and_channels_cache.cache.get(guild_id);

		if (
			!roles_and_channels_cache.channels?.find(
				(val) => val.id === display_data.channel_id
			)
		) {
			await this.fetchGuildRolesAndChannelsAndUpdateState(guild_id, true);
		}

		const listing_cache =
			this.state.poll_listings_cache.cache.get(guild_id);

		const poll_index = listing_cache.findIndex(
			(val) => val.message_id === display_data.message_id
		);

		if (poll_index > -1) {
			listing_cache[poll_index] = display_data;
		} else {
			listing_cache.push(display_data);
		}

		this.setState({
			poll_listings_cache: {
				currently_being_fetched:
					this.state.poll_listings_cache.currently_being_fetched,
				cache: this.state.poll_listings_cache.cache,
			},
		});
	}

	fetchPollDataAndUpdateState(guild_id: string, message_id?: string) {
		// should fetch if never fetched before OR if it is an edit

		const should_fetch =
			Boolean(!this.state.poll_data_cache.cache.has(guild_id)) ||
			Boolean(message_id);

		if (!should_fetch) {
			//console.log(guild_id, `Poll Data Cache`);
			return;
		}

		if (this.state.poll_data_cache.currently_being_fetched) {
			//console.log(guild_id, "Poll Data Cache Duplicate Fetch (Aborting)");
			return;
		}

		// update it instantly because setState is not guaranteed to be instant
		this.state.poll_data_cache.currently_being_fetched = true;

		this.setState({
			poll_data_cache: {
				currently_being_fetched: true,
				cache: this.state.poll_data_cache.cache,
			},
		});

		//console.log(guild_id, `Fetching Poll Data`);
		fetchPollData(this.state.discord_auth_token, guild_id, message_id).then(
			(val) => {
				if (!val) {
					this.props.history.replace(
						common_utils.getGuildOptionsURL(guild_id)
					);
				} else {
					this.state.poll_data_cache.cache.set(guild_id, val);
				}

				this.setState({
					poll_data_cache: {
						currently_being_fetched: false,
						cache: this.state.poll_data_cache.cache,
					},
				});
			}
		);
	}

	fetchEventDataAndUpdateState(guild_id: string, message_id?: string) {
		// should fetch if never fetched before OR if it is an edit
		const should_fetch =
			Boolean(!this.state.event_data_cache.cache.has(guild_id)) ||
			Boolean(message_id);
		if (!should_fetch) {
			//console.log(guild_id, `Event Data Cache`);
			return;
		}

		if (this.state.event_data_cache.currently_being_fetched) {
			// console.log(
			// 	guild_id,
			// 	"Event Data Cache Duplicate Fetch (Aborting)"
			// );
			return;
		}

		// update it instantly because setState is not guaranteed to be instant
		this.state.event_data_cache.currently_being_fetched = true;

		this.setState({
			event_data_cache: {
				currently_being_fetched: true,
				cache: this.state.event_data_cache.cache,
			},
		});

		//console.log(guild_id, `Fetching Event Data`);
		fetchEventData(
			this.state.discord_auth_token,
			guild_id,
			message_id
		).then((val) => {
			if (!val) {
				this.props.history.replace(
					common_utils.getGuildOptionsURL(guild_id)
				);
			} else {
				this.state.event_data_cache.cache.set(guild_id, val);
			}
			this.setState({
				event_data_cache: {
					currently_being_fetched: false,
					cache: this.state.event_data_cache.cache,
				},
			});
		});
	}

	fetchGuildSettingsAndUpdateState(guild_id: string) {
		//console.log("Fetching Guild Settings");
		fetchGuildSettings(this.state.discord_auth_token, guild_id).then(
			(val) =>
				this.setState({
					guild_settings_data: val,
				})
		);
	}

	fetchGuildChannelSettingsAndUpdateState(guild_id: string) {
		//console.log("Fetching Guild Channel Settings");
		fetchGuildChannelSettings(this.state.discord_auth_token, guild_id).then(
			(val) =>
				this.setState({
					guild_channel_settings_overrides: val,
				})
		);
	}

	handleRoutes() {
		let on_save_func = (
			settings: GuildSettings,
			individual_channel_settings_overrides?: GuildEntityColumns,
			channel_id?: string,
			guild_id?: string,
			refetch_channels?: boolean
		) => {
			if (settings) {
				let new_cache_data: GuildSettingsProps = {
					...this.state.guild_settings_data,
					original_guild_settings: settings,
					initial_event_channel_id: settings.event_channel?.id,
					initial_poll_channel_id: settings.poll_channel?.id,
				};

				this.setState({
					guild_settings_data: new_cache_data,
				});
			}

			if (individual_channel_settings_overrides) {
				if (refetch_channels && guild_id) {
					this.fetchGuildRolesAndChannelsAndUpdateState(
						guild_id,
						true
					);
				}

				let new_cache_data: GuildChannelSettingsOverrides = cloneDeep(
					this.state.guild_channel_settings_overrides
				);
				new_cache_data.overrides[channel_id] =
					individual_channel_settings_overrides;

				this.setState({
					guild_channel_settings_overrides: new_cache_data,
				});
			}
		};

		if (!this.state.user_data || !this.state.list_of_guilds_data?.list) {
			return <>{DashboardUtils.RenderFullLoadingThrobberPage()}</>;
		}

		return (
			<div>
				<GuildListContext.Provider
					value={this.state.list_of_guilds_data?.list ?? []}
				>
					<UserContext.Provider
						value={{
							...this.state.user_data,
							...this.state.discord_auth_token,
						}}
					>
						<Switch>
							<Route exact={true} path={"/dashboard/admin"}>
								<AdminPage />
							</Route>
							<Route exact={true} path={"/dashboard/admin/entities"}>
								<AdminEntitiesPage />
							</Route>
							<Route exact={true} path={"/dashboard/admin/stripe"}>
								<AdminStripePage />
							</Route>
							<Route exact={true} path={"/dashboard/admin/status"}>
								<AdminStatusPageWrapper />
							</Route>
							<Route exact={true} path={"/dashboard/admin/waitlist"}>
								<AdminWaitlistPage />
							</Route>
							<Route exact={true} path={"/dashboard/admin/logs"}>
								<AdminLogsPage />
							</Route>
							<Route exact={true} path={"/dashboard/admin/stats"}>
								<AdminStatsPage />
							</Route>
							<Route exact={true} path={"/dashboard/admin/shards"}>
								<AdminShardsPage />
							</Route>
							<Route exact={true} path={"/dashboard/admin/blurbs"}>
								<AdminBlurbsPage />
							</Route>
							<Route exact={true} path={"/dashboard/admin/entitlements"}>
								<AdminEntitlementsPage />
							</Route>
							<Route
								exact={true}
								path={"/dashboard/admin/bulk-delete-events"}
							>
								<AdminBulkDeleteEventsPage />
							</Route>
							<Route
								exact={true}
								path={"/dashboard/admin/delete-user-data"}
							>
								<AdminDeleteUserDataPage />
							</Route>
							<Route
								path={this.PathData.premium.path}
								exact={this.PathData.premium.exact}
							>
								<PremiumPage
									back_link={
										(this.props.location.state as any)
											?.prev_path
									}
								/>
							</Route>
							<Route
								path={this.PathData.stripe_redirect.path}
								exact={this.PathData.stripe_redirect.exact}
							>
								<StripeRedirectPage />
							</Route>
							<Route
								path={this.PathData.contact_us.path}
								exact={this.PathData.contact_us.exact}
							>
								<SeshProContactPage />
							</Route>
							<Route
								exact={this.PathData.google_redirect.exact}
								path={this.PathData.google_redirect.path}
							>
								<GoogleRedirectPage />
							</Route>
							<Route
								exact={this.PathData.guild_options.exact}
								path={this.PathData.guild_options.path}
							>
								<InitGuildOptions
									list_of_guilds={
										this.state.list_of_guilds_data.list
									}
									auth_token={this.state.discord_auth_token}
								/>
							</Route>
							<Route
								exact={this.PathData.guild_settings.exact}
								path={this.PathData.guild_settings.path}
							>
								<InitGuildSettings
									discord_auth_token={
										this.state.discord_auth_token
									}
									settings_data={
										this.state.guild_settings_data
									}
									roles_and_channels_cache={
										this.state
											.guild_roles_and_channels_cache
											.cache
									}
									clear_settings_data_func={() =>
										this.setState({
											guild_settings_data: null,
										})
									}
									on_save_func={on_save_func}
									on_premium_modified_func={(props) => {
										const guild_in_guild_data_list =
											this.state.list_of_guilds_data.list.find(
												(val) =>
													val.id ===
													props.guild_data.id
											);

										guild_in_guild_data_list.is_premium =
											props.is_premium;

										const event_data_cache =
											this.state.event_data_cache?.cache?.get(
												props.guild_data.id
											);

										if (event_data_cache) {
											event_data_cache.is_premium =
												props.is_premium;
										}

										const poll_data_cache =
											this.state.poll_data_cache?.cache?.get(
												props.guild_data.id
											);

										if (poll_data_cache) {
											poll_data_cache.is_premium =
												props.is_premium;
										}

										this.setState({
											guild_settings_data: props,
											list_of_guilds_data:
												this.state.list_of_guilds_data,
											event_data_cache:
												this.state.event_data_cache,
											poll_data_cache:
												this.state.poll_data_cache,
										});
									}}
								/>
							</Route>
							<Route
								exact={
									this.PathData.guild_calendar_settings.exact
								}
								path={
									this.PathData.guild_calendar_settings.path
								}
							>
								<CalendarSettingsPage />
							</Route>
							<Route
								exact={
									this.PathData.guild_channel_settings_picker
										.exact
								}
								path={
									this.PathData.guild_channel_settings_picker
										.path
								}
							>
								<InitGuildChannelPicker
									list_of_guilds={
										this.state.list_of_guilds_data.list
									}
									discord_auth_token={
										this.state.discord_auth_token
									}
									channel_settings_data={
										this.state
											.guild_channel_settings_overrides
									}
									roles_and_channels_cache={
										this.state
											.guild_roles_and_channels_cache
											.cache
									}
									clear_settings_data_func={() =>
										this.setState({
											guild_channel_settings_overrides:
												null,
										})
									}
									on_save_func={(
										deleted_override_channel_id
									) => {
										let new_cache_data: GuildChannelSettingsOverrides =
											cloneDeep(
												this.state
													.guild_channel_settings_overrides
											);

										delete new_cache_data.overrides[
											deleted_override_channel_id
										];

										this.setState({
											guild_channel_settings_overrides:
												new_cache_data,
										});
									}}
								/>
							</Route>
							<Route
								exact={
									this.PathData.guild_channel_settings.exact
								}
								path={this.PathData.guild_channel_settings.path}
							>
								<InitGuildChannelSettings
									discord_auth_token={
										this.state.discord_auth_token
									}
									channel_settings_overrides={
										this.state
											.guild_channel_settings_overrides
									}
									roles_and_channels_cache={
										this.state
											.guild_roles_and_channels_cache
											.cache
									}
									clear_settings_data_func={() =>
										this.setState({
											guild_channel_settings_overrides:
												null,
										})
									}
									guild_settings_data={
										this.state.guild_settings_data
									}
									on_save_func={on_save_func}
								/>
							</Route>
							<Route // create event
								exact={this.PathData.event_creation.exact}
								path={this.PathData.event_creation.path}
							>
								<InitEventCreation
									key="create_event"
									discord_auth_token={
										this.state.discord_auth_token
									}
									event_data_cache={
										this.state.event_data_cache.cache
									}
									roles_and_channels_cache={
										this.state
											.guild_roles_and_channels_cache
											.cache
									}
									is_edit={false}
									on_event_edited_func={
										this.onEventCreatedOrEdited
									}
								/>
							</Route>
							<Route // edit event
								exact={this.PathData.event_editing.exact}
								path={this.PathData.event_editing.path}
							>
								<InitEventCreation
									key="edit_event"
									discord_auth_token={
										this.state.discord_auth_token
									}
									event_data_cache={
										this.state.event_data_cache.cache
									}
									roles_and_channels_cache={
										this.state
											.guild_roles_and_channels_cache
											.cache
									}
									is_edit={true}
									on_event_edited_func={
										this.onEventCreatedOrEdited
									}
									on_unmount={(server_id) => {
										this.state.event_data_cache.cache.delete(
											server_id
										);
										this.setState({
											event_data_cache: {
												cache: this.state
													.event_data_cache.cache,
												currently_being_fetched:
													this.state.event_data_cache
														.currently_being_fetched,
											},
										});
									}}
								/>
							</Route>
							<Route // event listings
								exact={this.PathData.event_listings.exact}
								path={this.PathData.event_listings.path}
							>
								<EventListBase />
							</Route>
							<Route
								exact={this.PathData.time_finder.exact}
								path={this.PathData.time_finder.path}
							>
								<TimeFinderDashboard type="base" />
							</Route>
							<Route
								exact={this.PathData.time_finder_create.exact}
								path={this.PathData.time_finder_create.path}
							>
								<TimeFinderDashboard type="create" />
							</Route>
							<Route
								exact={this.PathData.time_finder_view.exact}
								path={this.PathData.time_finder_view.path}
							>
								<TimeFinderDashboard type="view" />
							</Route>
							<Route
								exact={this.PathData.time_finder_list.exact}
								path={this.PathData.time_finder_list.path}
							>
								<TimeFinderDashboard type="view" />
							</Route>
							<Route
								exact={this.PathData.time_finder_edit.exact}
								path={this.PathData.time_finder_edit.path}
							>
								<TimeFinderDashboard type="edit" />
							</Route>
							<Route
								exact={this.PathData.dst_fix.exact}
								path={this.PathData.dst_fix.path}
							>
								<EventListingFormWithDSTFix />
							</Route>
							<Route // event attendees
								exact={this.PathData.event_attendees.exact}
								path={this.PathData.event_attendees.path}
							>
								<RSVPDisplayFormPage />
							</Route>
							<Route // create poll
								exact={this.PathData.poll_creation.exact}
								path={this.PathData.poll_creation.path}
							>
								<InitPollCreation
									key="create_poll"
									discord_auth_token={
										this.state.discord_auth_token
									}
									poll_data_cache={
										this.state.poll_data_cache.cache
									}
									roles_and_channels_cache={
										this.state
											.guild_roles_and_channels_cache
											.cache
									}
									is_edit={false}
									on_poll_edited_func={
										this.onPollCreatedOrEdited
									}
								/>
							</Route>
							<Route // edit poll
								exact={this.PathData.poll_editing.exact}
								path={this.PathData.poll_editing.path}
							>
								<InitPollCreation
									key="edit_poll"
									discord_auth_token={
										this.state.discord_auth_token
									}
									poll_data_cache={
										this.state.poll_data_cache.cache
									}
									roles_and_channels_cache={
										this.state
											.guild_roles_and_channels_cache
											.cache
									}
									is_edit={true}
									on_poll_edited_func={
										this.onPollCreatedOrEdited
									}
									on_unmount={(server_id) => {
										this.state.poll_data_cache.cache.delete(
											server_id
										);

										this.setState({
											poll_data_cache: {
												cache: this.state
													.poll_data_cache.cache,
												currently_being_fetched:
													this.state.poll_data_cache
														.currently_being_fetched,
											},
										});
									}}
								/>
							</Route>
							<Route // poll listings
								exact={this.PathData.poll_listings.exact}
								path={this.PathData.poll_listings.path}
							>
								<InitPollListings />
							</Route>
							<Route
								exact={this.PathData.poll_results.exact}
								path={this.PathData.poll_results.path}
							>
								<InitPollResults />
							</Route>

							<Route
								exact={this.PathData.guild_select.exact}
								path={this.PathData.guild_select.path}
							>
								<InitGuildSelect
									discord_auth_token={
										this.state.discord_auth_token
									}
									list_of_guilds={
										this.state.list_of_guilds_data.list
									}
									sesh_client_id={this.props.sesh_client_id}
								/>
							</Route>

							<Route exact={false} path={"/dashboard/"}>
								<DashboardUtils.RenderDashboardErrorPage
									text="Page not found"
									title="404"
								/>
							</Route>
						</Switch>
					</UserContext.Provider>
				</GuildListContext.Provider>
			</div>
		);
	}

	render() {
		// store guild data on sesh router, only fetch if we're on a page that needs it (same with guild members)
		// if cache is empty, fetch regardless
		if (!this.state) {
			DashboardUtils.RenderFullLoadingThrobberPage();
			return;
		}

		return (
			<UserPreferencesModalContext.Provider
				value={{
					is_open: this.state.is_user_preferences_open,
					setIsOpen: (is_open: boolean) =>
						this.setState({ is_user_preferences_open: is_open }),
				}}
			>
				<div>
					<SeshToaster />
					<Navbar
						type="dashboard"
						user_data={this.state.user_data}
						sesh_client_id={this.props.sesh_client_id}
						discord_auth_token={this.state.discord_auth_token}
					/>
					<StatusBanner />
					<GuildRolesAndChannelContext.Provider
						value={this.state.guild_roles_and_channels_cache.cache}
					>
						<GuildMemberContext.Provider
							value={{
								members: this.state.members,
								getMembers: (guild_id) => {
									return (
										this.state.members.get(guild_id) ?? []
									);
								},
								addMembers: (guild_id, members) => {
									this.setState((state) => {
										const existing_members =
											state.members.get(guild_id) ?? [];

										const existing_members_map: Record<
											string,
											Mentionable
										> = {};

										const all_members =
											existing_members.concat(members);
										for (const member of all_members) {
											existing_members_map[member.id] =
												member;
										}

										state.members.set(
											guild_id,
											Object.values(existing_members_map)
										);

										const map_clone = new Map(
											state.members
										);

										return {
											...state,
											members: map_clone,
										};
									});
								},
							}}
						>
							<BlurbWrapper>{this.handleRoutes()}</BlurbWrapper>
							<div className="section"></div>
						</GuildMemberContext.Provider>
					</GuildRolesAndChannelContext.Provider>
				</div>
				<UserPreferencesModal />
			</UserPreferencesModalContext.Provider>
		);
	}
}

export default withRouter(SeshRouter);
